import ChatBubbleIcon from "@mui/icons-material/ChatBubble";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  IconButton,
  LinearProgress,
  Link,
  Stack,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink } from "react-router-dom";
import { match } from "ts-pattern";

import type { AssessmentId, GroupCategory } from "@thalamos/haven-common";

import {
  RatingBadge,
  useAnswerWords,
} from "../../components/RatingBadge/RatingBadge.js";
import { createRoute } from "../../routing/createRoute.js";
import { VISUALLY_HIDDEN } from "../../theme/visuallyHidden.js";
import {
  domainActionOf,
  type OverviewAnswer,
  type OverviewDomain,
  type OverviewItem,
} from "./overview_domains.js";
import { ProgressMark, type ProgressMarkKind } from "./ProgressMark.js";

/** The overview laid out across the page, or stacked for a phone. */
export type OverviewLayout = "wide" | "narrow";

const markOf = ({
  state,
  missingComments,
}: OverviewDomain): ProgressMarkKind =>
  missingComments > 0
    ? "begun"
    : match(state)
        .with("completed", (): ProgressMarkKind => "done")
        .with("in_progress", (): ProgressMarkKind => "begun")
        .with("not_started", "not_opened", (): ProgressMarkKind => "not_begun")
        .exhaustive();

const itemMarkOf = ({ answer, done }: OverviewItem): ProgressMarkKind =>
  done ? "done" : answer === undefined ? "not_begun" : "begun";

const percentOf = ({ done, total }: OverviewDomain) =>
  total === 0 ? 0 : Math.round((done / total) * 100);

/** Where the domain stands, in grey, or in bold red while comments are missing. */
const StatusLine = ({ domain }: { domain: OverviewDomain }) => {
  const { t } = useTranslation();

  return domain.missingComments > 0 ? (
    <Typography
      variant="caption"
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        color: "error.main",
        fontWeight: 700,
      }}
    >
      <ChatBubbleOutlineIcon sx={{ fontSize: 14 }} aria-hidden />
      {t("assessment.overview.requiresComments", {
        count: domain.missingComments,
      })}
    </Typography>
  ) : (
    <Typography variant="caption" component="span" color="text.secondary">
      {t(`assessment.overview.state.${domain.state}`)}
    </Typography>
  );
};

/** An item's answer: its rating as a badge in its domain's colours, or the option it chose worded. */
const ItemAnswer = ({
  answer,
  category,
}: {
  answer: OverviewAnswer | undefined;
  category: GroupCategory | undefined;
}) => {
  const { t } = useTranslation();
  const wordOf = useAnswerWords();

  if (answer === undefined) {
    return (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ fontStyle: "italic", flex: "none" }}
      >
        {t("assessment.overview.notRated")}
      </Typography>
    );
  }

  return match(answer)
    .with({ kind: "rated" }, ({ rating, scale }) => (
      <>
        <RatingBadge
          category={category}
          rating={rating}
          scale={scale}
          size={scale === "yes_no" ? "wide" : "small"}
        />
        <Box component="span" sx={VISUALLY_HIDDEN}>
          {wordOf({ scale, rating })}
        </Box>
      </>
    ))
    .with({ kind: "worded" }, ({ text }) => (
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ flex: "none" }}
      >
        {text}
      </Typography>
    ))
    .exhaustive();
};

const CommentMark = ({ item }: { item: OverviewItem }) => {
  const { t } = useTranslation();

  return match(item.comment)
    .with("missing", () => (
      <ChatBubbleOutlineIcon
        titleAccess={t("assessment.overview.commentRequired")}
        sx={{ fontSize: 16, color: "error.main", flex: "none" }}
      />
    ))
    .with("written", () => (
      <ChatBubbleIcon
        titleAccess={t("assessment.overview.commentAdded")}
        sx={{ fontSize: 16, color: "grey.500", flex: "none" }}
      />
    ))
    .with("not_required", () => null)
    .exhaustive();
};

/** Each item of a domain, its title leading straight to its question. */
const ItemList = ({
  assessmentId,
  domain,
  layout,
}: {
  assessmentId: AssessmentId;
  domain: OverviewDomain;
  layout: OverviewLayout;
}) => (
  <Box
    component="ul"
    sx={{
      listStyle: "none",
      m: 0,
      p: 0,
      ...(layout === "narrow"
        ? { pl: 1.25, borderLeft: 1, borderColor: "divider" }
        : {}),
    }}
  >
    {domain.items.map((item) => (
      <Box component="li" key={item.item}>
        <Link
          component={RouterLink}
          to={createRoute.assessmentItem(assessmentId, domain.group, item.item)}
          underline="hover"
          color="text.primary"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1,
            minHeight: 40,
            px: layout === "wide" ? 1 : 0,
            borderRadius: 0.75,
            fontSize: 14,
            "&:hover": layout === "wide" ? { bgcolor: "action.hover" } : {},
          }}
        >
          <ProgressMark kind={itemMarkOf(item)} size={14} />
          <Box component="span" sx={{ flex: "1 1 auto", minWidth: 0 }}>
            {item.title}
          </Box>
          <CommentMark item={item} />
          <ItemAnswer answer={item.answer} category={domain.category} />
        </Link>
      </Box>
    ))}
  </Box>
);

const DomainActionButton = ({
  assessmentId,
  domain,
  layout,
}: {
  assessmentId: AssessmentId;
  domain: OverviewDomain;
  layout: OverviewLayout;
}) => {
  const { t } = useTranslation();
  const action = domainActionOf(domain.state);

  return (
    <Button
      component={RouterLink}
      to={createRoute.assessmentDomain(assessmentId, domain.group)}
      variant={action === "continue" ? "contained" : "outlined"}
      aria-label={t(`assessment.overview.actionLabel.${action}`, {
        domain: domain.title,
      })}
      fullWidth={layout === "narrow"}
      sx={
        layout === "narrow"
          ? { minHeight: 44 }
          : { flex: "none", minWidth: 92, minHeight: 40 }
      }
    >
      {layout === "narrow"
        ? t(`assessment.overview.actionDomain.${action}`)
        : t(`assessment.overview.action.${action}`)}
    </Button>
  );
};

/**
 * One domain of the overview: where it stands and what to do next, and, when
 * expanded, its items, each leading to its own question.
 */
export const OverviewDomainRow = ({
  assessmentId,
  domain,
  expanded,
  layout,
  onToggle,
}: {
  assessmentId: AssessmentId;
  domain: OverviewDomain;
  expanded: boolean;
  layout: OverviewLayout;
  onToggle: () => void;
}) => {
  const { t } = useTranslation();
  const itemsId = `overview-domain-${domain.group}-items`;
  const chevron = (
    <ExpandMoreIcon
      aria-hidden
      sx={{
        color: "text.secondary",
        transition: "transform 150ms",
        transform: expanded ? "rotate(180deg)" : "none",
      }}
    />
  );

  return layout === "wide" ? (
    <Box
      component="li"
      sx={{
        borderBottom: 1,
        borderColor: "divider",
        "&:last-of-type": { borderBottom: 0 },
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        spacing={1.5}
        sx={{
          minHeight: 60,
          py: 0.75,
          pl: 1,
          pr: 2,
          bgcolor: expanded ? "action.hover" : undefined,
        }}
      >
        <IconButton
          onClick={onToggle}
          aria-expanded={expanded}
          aria-controls={itemsId}
          aria-label={t(
            expanded
              ? "assessment.overview.hideItems"
              : "assessment.overview.showItems",
            { domain: domain.title },
          )}
        >
          {chevron}
        </IconButton>
        <ProgressMark kind={markOf(domain)} />
        <Stack sx={{ flex: "1 1 auto", minWidth: 0 }} spacing={0.25}>
          <Typography sx={{ fontWeight: 600 }}>{domain.title}</Typography>
          <StatusLine domain={domain} />
        </Stack>
        <Stack sx={{ flex: "none", width: 140 }} spacing={0.5}>
          <Typography variant="caption" color="text.secondary">
            {t("assessment.overview.domainItemsOf", {
              done: domain.done,
              total: domain.total,
            })}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={percentOf(domain)}
            aria-hidden
            sx={{ height: 4, borderRadius: 2 }}
          />
        </Stack>
        <DomainActionButton
          assessmentId={assessmentId}
          domain={domain}
          layout={layout}
        />
      </Stack>
      <Collapse in={expanded} id={itemsId}>
        <Box sx={{ pl: 7.5, pr: 2, pb: 1.5, bgcolor: "action.hover" }}>
          <ItemList
            assessmentId={assessmentId}
            domain={domain}
            layout={layout}
          />
        </Box>
      </Collapse>
    </Box>
  ) : (
    <Box component="li" sx={{ borderBottom: 1, borderColor: "divider" }}>
      <ButtonBase
        onClick={onToggle}
        aria-expanded={expanded}
        aria-controls={itemsId}
        sx={{
          width: "100%",
          minHeight: 60,
          px: 2,
          py: 1,
          gap: 1.5,
          justifyContent: "flex-start",
          textAlign: "left",
          bgcolor: expanded ? "action.hover" : "background.paper",
        }}
      >
        <ProgressMark kind={markOf(domain)} />
        <Stack sx={{ flex: "1 1 auto", minWidth: 0 }} spacing={0.5}>
          <Typography sx={{ fontWeight: 600, fontSize: 15 }}>
            {domain.title}
          </Typography>
          <LinearProgress
            variant="determinate"
            value={percentOf(domain)}
            aria-hidden
            sx={{ width: "100%", maxWidth: 180, height: 3, borderRadius: 2 }}
          />
          <StatusLine domain={domain} />
        </Stack>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ flex: "none", fontVariantNumeric: "tabular-nums" }}
        >
          {t("assessment.overview.domainItemsOfShort", {
            done: domain.done,
            total: domain.total,
          })}
        </Typography>
        {chevron}
      </ButtonBase>
      <Collapse in={expanded} id={itemsId}>
        <Stack
          spacing={1.25}
          sx={{ pl: 5.75, pr: 2, pb: 1.75, bgcolor: "action.hover" }}
        >
          <ItemList
            assessmentId={assessmentId}
            domain={domain}
            layout={layout}
          />
          <DomainActionButton
            assessmentId={assessmentId}
            domain={domain}
            layout={layout}
          />
        </Stack>
      </Collapse>
    </Box>
  );
};
